import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Divider, Stack, Typography} from '@mui/material';
import TitleField from './documentHeader/TitleField';
import DateTimeField from './documentHeader/DateTimeField';
import MenuField from './documentHeader/MenuField';
import MultiSelectMenuField from './documentHeader/MultiSelectMenuField';
import ClassificationField from './documentHeader/ClassificationField';
import AuthorsField from './documentHeader/AuthorsField';
import {
    COMPARTMENT_OPTIONS,
    CONFIDENCE_OPTIONS,
    DEFAULT_HEADER_DATA,
    DISTRIBUTION_OPTIONS,
    IFC_OPTIONS,
    ORIGINATOR_OPTIONS,
    PRODUCTION_CENTER_OPTIONS
} from './documentHeader/headerFieldOptions';
import {updateDocumentHeaderData} from '../firestore/documents';
import {
    HEADER_META_FIELDS,
    normalizeHeaderValue,
    readHeaderFromYDocMap,
    toPlainYValue,
} from '../../../utils/documentHeaderMeta';
import EntityTagsSection, {TAGS_SECTION_HEADING_SX} from '../../../features/collection-requests/tags/EntityTagsSection';

const SECTION_HEADING_SX = TAGS_SECTION_HEADING_SX;

/**
 * Standalone Document Header Component
 * Sits above the ProseMirror editor as a separate React component.
 * All header data is read from and written to yDocMeta (Y.Map).
 * Firestore is updated as a persistence side-effect on local writes only.
 *
 * @param {Object} props
 * @param {string} props.documentId - Firestore document ID (used only for persistence writes)
 * @param {Function} props.onChange - Callback when header data changes
 * @param {Object} props.yDocMeta - Y.Map reference for collaborative header metadata (source of truth)
 * @param {Object} props.ydoc - Y.Doc reference for creating transactions
 */
export default function DocumentHeader({documentId, onChange, yDocMeta, ydoc, headerMetaTick = 0}) {
    // Draft FINTEL authors tag their own report; no CM/similarity gate.
    const [title, setTitle] = useState(() => yDocMeta?.get('title') ?? '');
    const [headerData, setHeaderData] = useState(() => ({
        ...DEFAULT_HEADER_DATA,
        ...readHeaderFromYDocMap(yDocMeta),
    }));
    const onChangeRef = useRef(onChange);

    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    // When yDocMeta becomes available or is hydrated after sync, seed state from it
    useEffect(() => {
        if (!yDocMeta) return;
        const storedTitle = yDocMeta.get('title');
        if (storedTitle !== undefined) setTitle(toPlainYValue(storedTitle));
        setHeaderData(prev => ({...DEFAULT_HEADER_DATA, ...prev, ...readHeaderFromYDocMap(yDocMeta)}));
    }, [yDocMeta, headerMetaTick]);


    // Subscribe to yDoc meta changes (real-time collaboration)
    useEffect(() => {
        if (!yDocMeta) return;

        const observer = (event, transaction) => {
            // Skip changes initiated by this component — we already applied them optimistically.
            // Do NOT skip other local transactions (e.g. 'classification-update' from the
            // classification gutter plugin) — those need to be reflected here too.
            if (transaction.origin === 'header-update') return;

            console.log('[DocumentHeader] Received yDoc meta changes (origin:', transaction.origin, ')');

            event.changes.keys.forEach((change, key) => {
                if (change.action === 'add' || change.action === 'update') {
                    const value = yDocMeta.get(key);
                    console.log(`[DocumentHeader] Remote change: ${key} =`, value);

                    if (key === 'title') {
                        setTitle(toPlainYValue(value));
                    } else if (HEADER_META_FIELDS.includes(key)) {
                        setHeaderData(prev => ({...prev, [key]: normalizeHeaderValue(key, value)}));
                    }
                }
            });
        };

        yDocMeta.observe(observer);
        return () => yDocMeta.unobserve(observer);
    }, [yDocMeta]);

    const updateHeaderData = useCallback(async (updates) => {
        const normalizedUpdates = Object.entries(updates).reduce((acc, [key, value]) => {
            acc[key] = normalizeHeaderValue(key, value);
            return acc;
        }, {});

        const newData = {...headerData, ...normalizedUpdates};
        setHeaderData(newData);

        const listener = onChangeRef.current;
        if (listener) {
            listener({title, ...newData});
        }

        // 1. Write to yDoc (real-time collaboration)
        if (yDocMeta && ydoc) {
            ydoc.transact(() => {
                Object.entries(normalizedUpdates).forEach(([key, value]) => {
                    yDocMeta.set(key, value);
                });
            }, 'header-update');
            console.log('[DocumentHeader] Updated yDoc meta with:', normalizedUpdates);
        }

        // 2. Persist to Firestore (searchability/persistence)
        if (documentId) {
            try {
                await updateDocumentHeaderData(documentId, normalizedUpdates);
                console.log('[DocumentHeader] Persisted header data to Firestore');
            } catch (error) {
                console.error('[DocumentHeader] Error persisting header data:', error);
            }
        }
    }, [headerData, title, documentId, yDocMeta, ydoc]);

    const handleTitleChange = useCallback((newTitle) => {
        setTitle(newTitle);

        const listener = onChangeRef.current;
        if (listener) {
            listener({title: newTitle, ...headerData});
        }
    }, [headerData]);

    const handleDateChange = useCallback((dtg) => {
        updateHeaderData({dtg});
    }, [updateHeaderData]);

    const handleMenuSelect = useCallback((field, value) => {
        updateHeaderData({[field]: value});
    }, [updateHeaderData]);

    return (
        <Stack spacing={4} p={3} pt={4} zIndex={10} bgcolor="white">
            <TitleField
                documentId={documentId}
                yDocMeta={yDocMeta}
                ydoc={ydoc}
                headerMetaTick={headerMetaTick}
                onChange={handleTitleChange}
            />

            <Stack spacing={2} p={2} bgcolor={'#F9F9F9'} borderRadius={1}>
                <Stack spacing={1}>
                    <Typography sx={SECTION_HEADING_SX}>Security</Typography>

                    <Stack spacing={1}>
                        <ClassificationField value={headerData.classification}/>

                        <MenuField
                            label="IFC"
                            field="ifc"
                            value={headerData.ifc}
                            options={IFC_OPTIONS}
                            onSelect={handleMenuSelect}
                        />

                        <MultiSelectMenuField
                            label="Distribution"
                            field="distribution"
                            value={headerData.distribution}
                            options={DISTRIBUTION_OPTIONS}
                            onSelect={handleMenuSelect}
                        />

                        <MultiSelectMenuField
                            label="Compartment"
                            field="compartment"
                            value={headerData.compartment}
                            options={COMPARTMENT_OPTIONS}
                            onSelect={handleMenuSelect}
                        />
                    </Stack>
                </Stack>

                <Divider/>

                <Stack spacing={1}>
                    <Typography sx={SECTION_HEADING_SX}>Production</Typography>

                    <Stack spacing={1}>
                        <AuthorsField authors={headerData.authors}/>

                        <DateTimeField
                            label="DTG"
                            value={headerData.dtg}
                            onChange={handleDateChange}
                        />

                        <MenuField
                            label="Originator"
                            field="originator"
                            value={headerData.originator}
                            options={ORIGINATOR_OPTIONS}
                            onSelect={handleMenuSelect}
                        />

                        <MenuField
                            label="Production Center"
                            field="productionCenter"
                            value={headerData.productionCenter}
                            options={PRODUCTION_CENTER_OPTIONS}
                            onSelect={handleMenuSelect}
                        />
                    </Stack>
                </Stack>

                <Divider/>

                <Stack spacing={1}>
                    <Typography sx={SECTION_HEADING_SX}>Assessment</Typography>

                    <Stack spacing={1}>
                        <MenuField
                            label="Confidence"
                            field="confidence"
                            value={headerData.confidence}
                            options={CONFIDENCE_OPTIONS}
                            onSelect={handleMenuSelect}
                        />
                    </Stack>
                </Stack>

                {documentId && (
                    <>
                        <Divider/>
                        <Stack spacing={1}>
                            <Typography sx={SECTION_HEADING_SX}>Tags</Typography>
                            <EntityTagsSection
                                entityType="REPORT"
                                entityId={documentId}
                                canEdit
                                enabled
                                dense
                                showHeading={false}
                            />
                        </Stack>
                    </>
                )}
            </Stack>
        </Stack>
    );
}
